import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';

import {
  AppBar, Avatar, Box, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemText, ListSubheader, Menu, MenuItem,
  Stack,
  Toolbar
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAppStore } from '@/store/app.store';
import { useAuth, useNavAccess } from '@/hooks';
import { useAuthStore } from '@/store/auth.store';
import { adminMenu as NAV_GROUPS } from '@/app/navigations/adminMenu'
import { useSettings } from '../contexts/SettingContext';
const DRAWER_WIDTH = 260;

// ─── Component ────────────────────────────────────────────────────────────────

export const AdminLayout: React.FC = () => {
  const settings = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const {sidebarOpen, toggleSidebar,} = useAppStore();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { canAccess } = useNavAccess();

  useEffect(() => {
    if (!isAuthenticated) navigate('/auth/login');
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;


  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/auth/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', }}>
      {/* Sidebar */}
      <Drawer
        variant="persistent"
        open={sidebarOpen}
        sx={{
          width: sidebarOpen ? DRAWER_WIDTH : 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box 
            sx={{ 
                fontSize: '1.5rem', 
                fontWeight: 700, 
                textDecoration: 'none',
            }} 
            component={RouterLink} to="/"
        >
            {settings.nickname}
          </Box>
        </Box>
        <List sx={{ px: 1, pb: 3 }}>
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(canAccess);
            if (!visibleItems.length) return null;
            return (
              <Box key={group.label} sx={{ mb: 1 }}>
                <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                  {group.label}
                </ListSubheader>
                {visibleItems.map((item) => {
                  const selected =
                    location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        selected={selected}
                        onClick={() => navigate(item.path)}
                        sx={{ borderRadius: 1 }}
                      >
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </Box>
            );
          })}
        </List>
      </Drawer>

      {/* Main content */}
      <Stack direction="column" sx={{ flex: 1 }}>
        <AppBar position="static" sx={{ bgcolor: '#fff', color: '#1f2937', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <IconButton edge="start" color="inherit" onClick={toggleSidebar} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            
            <Box sx={{ flex: 1 }} />

            {/* User menu */}
            <Avatar
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ width: 40, height: 40, cursor: 'pointer' }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>{user?.name}</Box>
                  <Box sx={{ fontSize: '0.85rem', color: '#6b7280' }}>{user?.email}</Box>
                </Box>
              </MenuItem>
              <MenuItem component={RouterLink} to="/admin/settings" onClick={() => setAnchorEl(null)}>
                <LogoutIcon sx={{ mr: 1 }} /> Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component={'main'} sx={{ flex: 1, p: 3, overflow: 'auto', maxWidth: sidebarOpen ? `calc(100vw - ${DRAWER_WIDTH}px)` : '100%' }}>
          <Outlet />
        </Box>
      </Stack>
    </Box>
  );
};

export default AdminLayout;